// src/components/Schedule.jsx

import React, { useEffect, useState, useMemo } from 'react'
import {
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  CircularProgress,
  Box,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Stack,
  Alert,
  Card,
  CardContent,
  Divider,
  Chip,
  useMediaQuery,
  useTheme,
  Button,
  Switch,
  FormControlLabel
} from '@mui/material'
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Assignment as ReportIcon,
  Download as DownloadIcon,
  Add as AddIcon,
  SwapHoriz as ConvertIcon,
  Celebration as SocialEventIcon
} from '@mui/icons-material'
import { formatGameDate, formatGameTime } from '../../utils/dateUtils'
import { useNavigate, Link } from 'react-router-dom'
import DeleteConfirmDialog from '../modals/DeleteConfirmDialog'
import MatchReportViewDialog from '../MatchReport/MatchReportViewDialog.jsx'
import unifiedApi from '../../utils/unifiedApi'
import { usePermissions } from '../../contexts/PermissionsContext'
import { useAuth } from '../../contexts/AuthContext'
import GameTypeBadge, { GAME_TYPE_LABELS } from './GameTypeBadge'
import SchedulePlaceholderForm from './SchedulePlaceholderForm'
import ConvertPlaceholderDialog from './ConvertPlaceholderDialog'
import { buildScheduleSections, getScheduleRowKey } from './scheduleWeeks'

const buildInitialRsvpState = (events) =>
  Object.fromEntries(
    events.map(ev => [ev.id, { attending: ev.currentUserAttending ?? null, submitting: false, error: null }])
  )

// Placeholders render as pseudo-game rows with TBD labels and no team links / sheets / reports.
const toPlaceholderRow = p => ({
  id: 'placeholder-' + p.placeholderId,
  placeholderId: p.placeholderId,
  _isPlaceholder: true,
  start: p.start,
  end: p.end,
  homeTeamId: null,
  awayTeamId: null,
  homeTeamName: p.homeLabel || 'TBD',
  awayTeamName: p.awayLabel || 'TBD',
  venueName: p.venueName,
  fieldName: p.fieldName,
  gameType: p.gameType ?? 0,
  description: p.description
})

const toSocialEventRow = s => ({
  id: 'social-' + s.id,
  socialEventId: s.id,
  _isSocialEvent: true,
  start: s.start,
  end: s.end,
  title: s.title,
  description: s.description,
  location: s.location,
  venueName: s.location,
  leagueId: s.leagueId,
  leagueName: s.leagueName,
  teamId: s.teamId,
  teamName: s.teamName,
  isLeagueWide: s.isLeagueWide,
  currentUserAttending: s.currentUserAttending ?? null,
})

const Schedule = ({ leagueId, teamId, limit, showFilter = true, scheduleData }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [games, setGames] = useState([])
  const [placeholders, setPlaceholders] = useState([])
  const [socialEvents, setSocialEvents] = useState([])
  const [socialRsvpState, setSocialRsvpState] = useState({}) // { [eventId]: { attending, submitting, error } }
  const [placeholderFormOpen, setPlaceholderFormOpen] = useState(false)
  const [editingPlaceholder, setEditingPlaceholder] = useState(null)
  const [convertDialogOpen, setConvertDialogOpen] = useState(false)
  const [convertingPlaceholder, setConvertingPlaceholder] = useState(null)
  const [leagueTeams, setLeagueTeams] = useState([])
  const [leagueVenuesAndFields, setLeagueVenuesAndFields] = useState({ venues: [], fields: [] })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [gameToDelete, setGameToDelete] = useState(null)
  const [selectedTeamId, setSelectedTeamId] = useState('')
  const [selectedGameType, setSelectedGameType] = useState('')
  const [showUpcomingOnly, setShowUpcomingOnly] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [showAllGames, setShowAllGames] = useState(false)
  const [reportDialogOpen, setReportDialogOpen] = useState(false)
  const [selectedReport, setSelectedReport] = useState({ gameId: null, teamId: null, teamName: '', leagueId: null })
  const navigate = useNavigate()
  const { canManageLeague, canManageTeam } = usePermissions()
  const { isAuthenticated } = useAuth()

  // Helper component to display team jersey color
  const TeamJerseyColor = ({ jerseyColorHex }) => {
    if (!jerseyColorHex) return null
    
    return (
      <Box
        sx={{
          width: 12,
          height: 12,
          backgroundColor: jerseyColorHex,
          borderRadius: '50%',
          border: '1px solid rgba(0,0,0,0.3)',
          flexShrink: 0
        }}
      />
    )
  }

  // Helper function to determine if a game has finished
  const isGameFinished = (game) => {
    const gameStart = new Date(game.start)
    const gameEnd = game.end ? new Date(game.end) : new Date(gameStart.getTime() + 60 * 60 * 1000) // Default to 1 hour after start
    return new Date() > gameEnd
  }

  // Helper function to get the canonical team URL
  const getTeamUrl = (teamId, teamSlug) => {
    if (teamSlug) {
      return `/teams/${teamSlug}`
    }
    return `/teams/${teamId}`
  }

  // Helper function to check if a team has submitted a match report
  const hasMatchReport = (game) => {
    // If scores are set, the game has been finalized and reports have been submitted
    return game.homeScore != null && game.awayScore != null
  }

  const isByeGame = (game) => (game.gameType ?? 0) === 5 || !game.awayTeamId

  const getGameDeleteLabel = (game) => (
    isByeGame(game)
      ? `${formatGameDate(game.start)} - ${game.homeTeamName} BYE`
      : `${formatGameDate(game.start)} - ${game.homeTeamName} vs ${game.awayTeamName}`
  )

  // Handle opening match report dialog
  const handleOpenReport = (gameId, teamId, teamName) => {
    setSelectedReport({ gameId, teamId, teamName, leagueId })
    setReportDialogOpen(true)
  }

  // Handle closing match report dialog
  const handleCloseReport = () => {
    setReportDialogOpen(false)
    setSelectedReport({ gameId: null, teamId: null, teamName: '', leagueId: null })
  }

  // Memoized derived values
  const { filteredGames, filteredPlaceholders, availableTeams, itemsPerPage, hasNonRegularGames } = useMemo(() => {
    // Apply team filter if a specific team is selected
    let filtered = games
    let filteredPlaceholderRows = placeholders || []

    const today = new Date()
    today.setUTCHours(0, 0, 0, 0)

    if (selectedTeamId) {
      filtered = filtered.filter(g => 
        g.homeTeamId === parseInt(selectedTeamId) || g.awayTeamId === parseInt(selectedTeamId)
      )
    }

    // Apply game type filter
    if (selectedGameType !== '') {
      filtered = filtered.filter(g => (g.gameType ?? 0) === parseInt(selectedGameType))
      filteredPlaceholderRows = filteredPlaceholderRows.filter(p => (p.gameType ?? 0) === parseInt(selectedGameType))
    }

    // Apply upcoming filter (today and future only) to both games and placeholders
    if (showUpcomingOnly) {
      filtered = filtered.filter(g => new Date(g.start) >= today)
      filteredPlaceholderRows = filteredPlaceholderRows.filter(p => new Date(p.start) >= today)
    }
    
    // Apply limit if specified
    if (limit && filtered.length > limit) {
      filtered = filtered.slice(0, limit)
    }

    // Extract unique teams from games for the dropdown (only show if showFilter is true and not already filtered by teamId)
    const teams = showFilter && !teamId && games.length > 0 ? (() => {
      const teamMap = new Map()
      games.forEach(game => {
        if (game.homeTeamId && game.homeTeamName) {
          teamMap.set(game.homeTeamId, game.homeTeamName)
        }
        if (game.awayTeamId && game.awayTeamName) {
          teamMap.set(game.awayTeamId, game.awayTeamName)
        }
      })
      return Array.from(teamMap.entries())
        .map(([id, name]) => ({ id, name }))
        .sort((a, b) => a.name.localeCompare(b.name))
    })() : []

    // Calculate items per page based on number of teams
    let calculatedItemsPerPage
    if (leagueId && !teamId) {
      // Extract unique teams to calculate pagination threshold
      const teamSet = new Set()
      games.forEach(game => {
        if (game.homeTeamId) teamSet.add(game.homeTeamId)
        if (game.awayTeamId) teamSet.add(game.awayTeamId)
      })
      const teamCount = teamSet.size
      calculatedItemsPerPage = teamCount > 0 ? teamCount * 6 : 20
    } else {
      // For team-specific schedules or when no league context, use a default
      calculatedItemsPerPage = 20
    }

    // Detect whether any non-regular game types exist (for showing the filter)
    const nonRegular = games.some(g => (g.gameType ?? 0) !== 0) || (placeholders || []).some(p => (p.gameType ?? 0) !== 0)

    return {
      filteredGames: filtered,
      filteredPlaceholders: filteredPlaceholderRows,
      availableTeams: teams,
      itemsPerPage: calculatedItemsPerPage,
      hasNonRegularGames: nonRegular
    }
  }, [games, placeholders, selectedTeamId, selectedGameType, showUpcomingOnly, limit, showFilter, teamId, leagueId])

  // Week headers come from the full schedule so numbers and date ranges don't change with filters.
  const scheduleSections = useMemo(() => buildScheduleSections([
    ...games,
    ...(placeholders || []).map(toPlaceholderRow),
    ...(socialEvents || []).map(toSocialEventRow)
  ]), [games, placeholders, socialEvents])

  // Compute whether user can show admin actions
  const canShowActions = useMemo(() => {
    if (!isAuthenticated) return false

    // Check if user can manage the league (if we have a leagueId prop or from game data)
    if (leagueId && canManageLeague(leagueId)) return true

    // If we don't have leagueId prop, check if user can manage leagues from the games
    return !leagueId && games.some(game => game.leagueId && canManageLeague(game.leagueId))
  }, [isAuthenticated, leagueId, canManageLeague, games])

  // Lazy-load teams + venues + fields for placeholder admin UI
  useEffect(() => {
    let cancelled = false
    const loadAdminLookups = async () => {
      if (!leagueId) return
      if (!canManageLeague(leagueId)) return
      try {
        const [teams, venues, fields] = await Promise.all([
          unifiedApi.getLeagueTeamsWithManagers(leagueId).catch(() => []),
          unifiedApi.getVenues().catch(() => []),
          unifiedApi.get('/api/fields').catch(() => [])
        ])
        if (cancelled) return
        setLeagueTeams(Array.isArray(teams) ? teams : (teams?.items || []))
        setLeagueVenuesAndFields({
          venues: Array.isArray(venues) ? venues : (venues?.items || []),
          fields: Array.isArray(fields) ? fields : (fields?.items || [])
        })
      } catch (e) {
        // Silently ignore — placeholder admin will still work without these
      }
    }
    loadAdminLookups()
    return () => { cancelled = true }
  }, [leagueId, canManageLeague])

  useEffect(() => {
    const fetchGames = async () => {
      // If scheduleData is already provided, use it instead of making an API call
      if (scheduleData) {
        setLoading(false)
        setError(null)
        const games = scheduleData?.games || scheduleData || []
        setGames(games)
        setPlaceholders(scheduleData?.placeholders || [])
        const events = scheduleData?.socialEvents || []
        setSocialEvents(events)
        setSocialRsvpState(buildInitialRsvpState(events))
        return
      }

      setLoading(true)
      setError(null)
      
      try {
        let gamesData
        if (teamId) {
          // Use the existing legacy endpoint for team games
          gamesData = await unifiedApi.get(`/api/games/team/${teamId}`)
        } else if (leagueId) {
          // Use the unified league schedule endpoint
          gamesData = await unifiedApi.get(`/api/leagues/${leagueId}/schedule`)
        } else {
          throw new Error('Schedule requires leagueId or teamId')
        }

        // Handle the response structure - it might be wrapped in a schedule object
        const games = gamesData?.games || gamesData || []
        setGames(games)
        setPlaceholders(gamesData?.placeholders || [])
        const events = gamesData?.socialEvents || []
        setSocialEvents(events)
        setSocialRsvpState(buildInitialRsvpState(events))
      } catch (error) {
        console.error('Failed to load schedule:', error)
        setError('Failed to load schedule. Please try again.')
      } finally {
        setLoading(false)
      }
    }

    fetchGames()
  }, [leagueId, teamId, scheduleData])

  const openDeleteDialog = game => {
    setGameToDelete(game)
    setDeleteDialogOpen(true)
  }

  const closeDeleteDialog = () => {
    setGameToDelete(null)
    setDeleteDialogOpen(false)
  }

  const handleConfirmDelete = async () => {
    if (!gameToDelete) return
    const gameId = gameToDelete.id || gameToDelete.gameId
    await unifiedApi.delete(`/api/games/${gameId}`)
    closeDeleteDialog()
    // refresh
    setGames(g => g.filter(gm => (gm.id || gm.gameId) !== gameId))
  }

  const downloadGameSheet = async (gameId, teamId) => {
    try {
      const blob = await unifiedApi.getBlob(`/api/games/${gameId}/teams/${teamId}/gamesheet`, {
        headers: {
          'Accept': 'application/pdf'
        }
      })
      
      if (blob) {
        const downloadUrl = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = downloadUrl
        link.download = `gamesheet-${gameId}-${teamId}.pdf`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(downloadUrl)
      } else {
        console.error('Failed to download game sheet: no data received')
      }
    } catch (error) {
      console.error('Error downloading game sheet:', error)
    }
  }

  if (loading) {
    return (
      <Box textAlign="center" my={2}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ my: 2 }}>
        {error}
      </Alert>
    )
  }

  if (games.length === 0 && (placeholders || []).length === 0 && (socialEvents || []).length === 0) {
    return (
      <Typography variant="body2" color="textSecondary" align="center" my={2}>
        No games scheduled.
      </Typography>
    )
  }

  // Convert placeholders and social events into pseudo-game rows so they merge naturally into the schedule.
  const placeholderRows = (filteredPlaceholders || []).map(toPlaceholderRow)

  // Filter by team if a team filter is active (only show team-scoped events for that team or
  // events that aren't team-scoped). Filter by upcoming if applicable.
  const today = new Date()
  today.setUTCHours(0, 0, 0, 0)
  const filteredSocialEvents = (socialEvents || []).filter(s => {
    if (selectedTeamId) {
      const teamIdNum = parseInt(selectedTeamId)
      if (s.teamId && s.teamId !== teamIdNum) return false
    }
    if (showUpcomingOnly && new Date(s.start) < today) return false
    return true
  })
  const socialEventRows = filteredSocialEvents.map(toSocialEventRow)

  const combinedFiltered = [...filteredGames, ...placeholderRows, ...socialEventRows]

  // Sort games by date, start time, venue, field ascending
  let sortedGames = [...combinedFiltered].sort((a, b) => {
    const dateA = new Date(a.start)
    const dateB = new Date(b.start)
    if (dateA < dateB) return -1
    if (dateA > dateB) return 1
    // If same date, compare time
    const timeA = dateA.getTime()
    const timeB = dateB.getTime()
    if (timeA < timeB) return -1
    if (timeA > timeB) return 1
    // If same time, compare venue
    if ((a.venueName || '') < (b.venueName || '')) return -1
    if ((a.venueName || '') > (b.venueName || '')) return 1
    // If same venue, compare field
    if ((a.fieldName || '') < (b.fieldName || '')) return -1
    if ((a.fieldName || '') > (b.fieldName || '')) return 1
    return 0
  })

  // Apply pagination if not showing all games and we have itemsPerPage set and no limit prop
  const totalRowCount = filteredGames.length + (filteredPlaceholders || []).length + filteredSocialEvents.length
  const shouldPaginate = Boolean(!limit && itemsPerPage > 0 && totalRowCount > itemsPerPage && !showAllGames)
  const totalPages = shouldPaginate ? Math.ceil(totalRowCount / itemsPerPage) : 1
  
  // Apply pagination
  if (shouldPaginate) {
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    sortedGames = sortedGames.slice(startIndex, endIndex)
  }

  // Insert a week header before the first row of the page and wherever the section changes,
  // so a week continuing from the previous page shows its header again.
  const displayRows = []
  let previousSectionKey = null
  sortedGames.forEach(game => {
    const section = scheduleSections.get(getScheduleRowKey(game))
    if (section && section.key !== previousSectionKey) {
      displayRows.push({ _isSectionHeader: true, id: `section-${section.key}-${displayRows.length}`, section })
    }
    previousSectionKey = section?.key ?? null
    displayRows.push(game)
  })
  const tableColumnCount = canShowActions ? 10 : 9

  // Handlers for pagination
  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage)
  }

  const handleShowAllToggle = (event) => {
    const value = event.target.value
    setShowAllGames(value === 'all')
    setCurrentPage(1) // Reset to first page when toggling
  }

  // Handlers for placeholder admin actions
  const handleAddPlaceholder = () => {
    setEditingPlaceholder(null)
    setPlaceholderFormOpen(true)
  }
  const handleEditPlaceholder = (game) => {
    const original = (placeholders || []).find(p => p.placeholderId === game.placeholderId)
    if (!original) return
    setEditingPlaceholder({
      id: original.placeholderId,
      start: original.start,
      end: original.end,
      venueId: original.venueId,
      fieldId: original.fieldId,
      homeLabel: original.homeLabel,
      awayLabel: original.awayLabel,
      gameType: original.gameType,
      isPublished: original.isPublished,
      description: original.description
    })
    setPlaceholderFormOpen(true)
  }
  const handleDeletePlaceholder = async (placeholderId) => {
    try {
      await unifiedApi.deleteSchedulePlaceholder(placeholderId)
      setPlaceholders(prev => prev.filter(p => p.placeholderId !== placeholderId))
    } catch (e) {
      console.error('Failed to delete placeholder:', e)
    }
  }
  const handleConvertPlaceholder = (game) => {
    const original = (placeholders || []).find(p => p.placeholderId === game.placeholderId)
    setConvertingPlaceholder({ ...original, id: original?.placeholderId })
    setConvertDialogOpen(true)
  }
  const handlePlaceholderSaved = (saved) => {
    // Reload by refetching schedule via parent (best-effort): patch optimistically.
    if (saved && saved.id) {
      setPlaceholders(prev => {
        const existing = prev.find(p => p.placeholderId === saved.id)
        const mapped = {
          placeholderId: saved.id,
          start: saved.start,
          end: saved.end,
          homeLabel: saved.homeLabel,
          awayLabel: saved.awayLabel,
          fieldName: prev.find(p => p.placeholderId === saved.id)?.fieldName,
          venueName: prev.find(p => p.placeholderId === saved.id)?.venueName,
          gameType: saved.gameType,
          description: saved.description,
          isPublished: saved.isPublished,
          venueId: saved.venueId,
          fieldId: saved.fieldId
        }
        if (existing) return prev.map(p => p.placeholderId === saved.id ? { ...p, ...mapped } : p)
        return [...prev, mapped]
      })
    }
  }
  const handlePlaceholderConverted = (game, placeholderId) => {
    setPlaceholders(prev => prev.filter(p => p.placeholderId !== placeholderId))
    if (game) {
      setGames(prev => [...prev, game])
    }
  }

  const canManageThisLeague = leagueId ? canManageLeague(leagueId) : false

  const handleSocialRsvp = async (eventId, attending) => {
    setSocialRsvpState(prev => ({
      ...prev,
      [eventId]: { ...prev[eventId], submitting: true, error: null },
    }))
    try {
      await unifiedApi.rsvpToSocialEvent(eventId, attending)
      setSocialRsvpState(prev => ({
        ...prev,
        [eventId]: { attending, submitting: false, error: null },
      }))
    } catch {
      setSocialRsvpState(prev => ({
        ...prev,
        [eventId]: { ...prev[eventId], submitting: false, error: 'Failed to save. Please try again.' },
      }))
    }
  }

  // Mobile card component
  const GameCard = ({ game }) => {
    if (game._isSocialEvent) {
      const scopeLabel = game.teamId
        ? `Team: ${game.teamName || ''}`
        : game.leagueId
          ? `League: ${game.leagueName || ''}`
          : ''
      const rsvp = socialRsvpState[game.socialEventId] ?? { attending: game.currentUserAttending, submitting: false, error: null }
      return (
        <Card sx={{ mb: 2, borderLeft: '3px solid', borderLeftColor: 'secondary.main' }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 }, '&:last-child': { pb: { xs: 2, sm: 3 } } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Box>
                <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600 }}>
                  {formatGameDate(game.start)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatGameTime(game.start)}
                </Typography>
              </Box>
              <Chip
                icon={<SocialEventIcon />}
                label="Social"
                size="small"
                color="secondary"
                variant="outlined"
                clickable={false}
                onClick={() => {}}
              />
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
              {game.title}
            </Typography>
            {game.location && (
              <Typography variant="body2" color="text.secondary">
                {game.location}
              </Typography>
            )}
            {scopeLabel && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {scopeLabel}
              </Typography>
            )}
            {game.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {game.description}
              </Typography>
            )}
            {isAuthenticated && (
              <Box sx={{ mt: 1.5 }}>
                {rsvp.error && <Alert severity="error" sx={{ py: 0, mb: 1 }}>{rsvp.error}</Alert>}
                {rsvp.attending === null ? (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <Button variant="contained" color="success" size="small" disabled={rsvp.submitting}
                      fullWidth={isMobile}
                      onClick={() => handleSocialRsvp(game.socialEventId, true)}>
                      I&apos;ll be there
                    </Button>
                    <Button variant="outlined" color="error" size="small" disabled={rsvp.submitting}
                      fullWidth={isMobile}
                      onClick={() => handleSocialRsvp(game.socialEventId, false)}>
                      Can&apos;t make it
                    </Button>
                  </Stack>
                ) : (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                    <Chip label={rsvp.attending ? 'Attending' : 'Not attending'}
                      color={rsvp.attending ? 'success' : 'error'} size="small" clickable={false} onClick={() => {}} />
                    <Button variant="text" size="small" disabled={rsvp.submitting}
                      fullWidth={isMobile}
                      onClick={() => handleSocialRsvp(game.socialEventId, !rsvp.attending)}>
                      Change to {rsvp.attending ? 'not attending' : 'attending'}
                    </Button>
                  </Stack>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      )
    }
    if (game._isPlaceholder) {
      return (
        <Card sx={{ mb: 2, borderLeft: '3px solid', borderLeftColor: 'warning.main' }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 }, '&:last-child': { pb: { xs: 2, sm: 3 } } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Box>
                <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600 }}>
                  {formatGameDate(game.start)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatGameTime(game.start)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                <GameTypeBadge gameType={game.gameType ?? 0} />
                <Chip label="TBD" size="small" color="warning" variant="outlined" clickable={false} onClick={() => {}} />
              </Box>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
              {game.homeTeamName} vs {game.awayTeamName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {game.fieldName && game.venueName
                ? game.fieldName + ' - ' + game.venueName
                : game.venueName || game.fieldName || 'Venue TBD'}
            </Typography>
            {game.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {game.description}
              </Typography>
            )}
            {canManageThisLeague && (
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
                <Tooltip title="Convert to game">
                  <IconButton size="small" color="primary" onClick={() => handleConvertPlaceholder(game)}>
                    <ConvertIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit placeholder">
                  <IconButton size="small" color="primary" onClick={() => handleEditPlaceholder(game)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete placeholder">
                  <IconButton size="small" color="error" onClick={() => handleDeletePlaceholder(game.placeholderId)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </CardContent>
        </Card>
      )
    }
    const finished = isGameFinished(game)
    const byeGame = isByeGame(game)
    return (
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 }, '&:last-child': { pb: { xs: 2, sm: 3 } } }}>
          {/* Date, Time, and Venue */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Box>
              <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600 }}>
                {formatGameDate(game.start)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatGameTime(game.start)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              <GameTypeBadge gameType={game.gameType ?? 0} />
              {(game.homeScore != null && game.awayScore != null) && (
                <Chip 
                  label="Final"
                  size="small"
                  color="primary"
                  variant="outlined"
                  clickable={false}
                  onClick={() => {/* do nothing */}}
                />
              )}
            </Box>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Teams */}
          <Box sx={{ mb: 2 }}>
            {/* Home Team */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Link 
                to={getTeamUrl(game.homeTeamId, game.homeTeamSlug)}
                style={{ textDecoration: 'none', color: 'inherit', flex: 1 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TeamJerseyColor jerseyColorHex={game.homeTeamJerseyColorHex} />
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {game.homeTeamName}
                  </Typography>
                </Box>
              </Link>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {game.homeScore != null && (
                  <Typography variant="h6" sx={{ fontWeight: 600, minWidth: '24px', textAlign: 'center' }}>
                    {game.homeScore}
                  </Typography>
                )}
                {!byeGame && !finished && (
                  <Tooltip title={`Download ${game.homeTeamName} Game Sheet`}>
                    <IconButton
                      size="small"
                      onClick={() => downloadGameSheet(game.id || game.gameId, game.homeTeamId)}
                    >
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {!byeGame && finished && (
                  hasMatchReport(game) ? (
                    <Tooltip title="View Match Report">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenReport(game.id || game.gameId, game.homeTeamId, game.homeTeamName)}
                      >
                        <ReportIcon fontSize="small" color="primary" />
                      </IconButton>
                    </Tooltip>
                  ) : canManageTeam(game.homeTeamId) && (
                    <Tooltip title="Submit Match Report">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/games/${game.id || game.gameId}/teams/${game.homeTeamId}/report`)}
                      >
                        <ReportIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )
                )}
              </Box>
            </Box>

            {/* Away Team */}
            {byeGame ? (
              <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                BYE
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Link 
                  to={getTeamUrl(game.awayTeamId, game.awayTeamSlug)}
                  style={{ textDecoration: 'none', color: 'inherit', flex: 1 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TeamJerseyColor jerseyColorHex={game.awayTeamJerseyColorHex} />
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {game.awayTeamName}
                    </Typography>
                  </Box>
                </Link>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {game.awayScore != null && (
                    <Typography variant="h6" sx={{ fontWeight: 600, minWidth: '24px', textAlign: 'center' }}>
                      {game.awayScore}
                    </Typography>
                  )}
                  {!finished && (
                    <Tooltip title={`Download ${game.awayTeamName} Game Sheet`}>
                      <IconButton
                        size="small"
                        onClick={() => downloadGameSheet(game.id || game.gameId, game.awayTeamId)}
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {finished && (
                    hasMatchReport(game) ? (
                      <Tooltip title="View Match Report">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenReport(game.id || game.gameId, game.awayTeamId, game.awayTeamName)}
                        >
                          <ReportIcon fontSize="small" color="primary" />
                        </IconButton>
                      </Tooltip>
                    ) : canManageTeam(game.awayTeamId) && (
                      <Tooltip title="Submit Match Report">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/games/${game.id || game.gameId}/teams/${game.awayTeamId}/report`)}
                        >
                          <ReportIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )
                  )}
                </Box>
              </Box>
            )}
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Venue */}
          <Typography variant="body2" color="text.secondary" sx={{ mb: canShowActions ? 2 : 0 }}>
            {game.fieldName && game.venueName 
              ? `${game.fieldName} - ${game.venueName}`
              : game.venueName || game.fieldName || 'Venue TBD'}
          </Typography>

          {/* Admin Actions */}
          {canShowActions && canManageLeague(leagueId || game.leagueId) && (
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Tooltip title="Edit Game">
                <IconButton
                  onClick={() => navigate(`/games/${game.id || game.gameId}/edit`)}
                  size="small"
                  color="primary"
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete Game">
                <IconButton 
                  onClick={() => openDeleteDialog(game)}
                  size="small"
                  color="error"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </CardContent>
      </Card>
    )
  }

  return (
    <>
      {canManageThisLeague && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddPlaceholder}
          >
            Add Placeholder
          </Button>
        </Box>
      )}
      {/* Filters and Controls */}
      {(showFilter || availableTeams.length > 0 || hasNonRegularGames || shouldPaginate) && (
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          spacing={2} 
          sx={{ mb: 2 }}
        >
          {showFilter && (
            <FormControlLabel
              control={
                <Switch
                  checked={showUpcomingOnly}
                  onChange={(e) => {
                    setShowUpcomingOnly(e.target.checked)
                    setCurrentPage(1)
                  }}
                />
              }
              label="Upcoming only"
            />
          )}

          {/* Team Filter Dropdown */}
          {availableTeams.length > 0 && (
            <FormControl 
              variant="outlined" 
              size="small" 
              sx={{ minWidth: { xs: '100%', sm: 200 } }}
            >
              <InputLabel>Filter by Team</InputLabel>
              <Select
                value={selectedTeamId}
                onChange={(e) => {
                  setSelectedTeamId(e.target.value)
                  setCurrentPage(1)
                }}
                label="Filter by Team"
              >
                <MenuItem value="">
                  <em>All Teams</em>
                </MenuItem>
                {availableTeams.map((team) => (
                  <MenuItem key={team.id} value={team.id}>
                    {team.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Game Type Filter Dropdown */}
          {hasNonRegularGames && (
            <FormControl 
              variant="outlined" 
              size="small" 
              sx={{ minWidth: { xs: '100%', sm: 180 } }}
            >
              <InputLabel>Game Type</InputLabel>
              <Select
                value={selectedGameType}
                onChange={(e) => {
                  setSelectedGameType(e.target.value)
                  setCurrentPage(1)
                }}
                label="Game Type"
              >
                <MenuItem value="">
                  <em>All Types</em>
                </MenuItem>
                {Object.entries(GAME_TYPE_LABELS).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          
          {/* Pagination Control Dropdown */}
          {shouldPaginate && (
            <FormControl 
              variant="outlined" 
              size="small" 
              sx={{ minWidth: { xs: '100%', sm: 150 } }}
            >
              <InputLabel>View</InputLabel>
              <Select
                value={showAllGames ? 'all' : 'paginated'}
                onChange={handleShowAllToggle}
                label="View"
              >
                <MenuItem value="paginated">
                  {isMobile ? `${itemsPerPage}/page` : `Paginated (${itemsPerPage} per page)`}
                </MenuItem>
                <MenuItem value="all">
                  {isMobile ? `All (${totalRowCount})` : `View All (${totalRowCount} games)`}
                </MenuItem>
              </Select>
            </FormControl>
          )}
        </Stack>
      )}
      
      {/* Mobile Card View */}
      {isMobile ? (
        <Box>
          {displayRows.map(game => {
            if (game._isSectionHeader) {
              return (
                <Typography
                  key={game.id}
                  variant="subtitle1"
                  component="h2"
                  data-testid="schedule-section-header"
                  sx={{
                    fontWeight: 700,
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    borderRadius: 1,
                    px: 2,
                    py: 1,
                    mt: 3,
                    mb: 1.5,
                    '&:first-of-type': { mt: 1 },
                    wordBreak: 'break-word'
                  }}
                >
                  {game.section.label}
                </Typography>
              )
            }
            const gameKey = getScheduleRowKey(game)
            return <GameCard key={gameKey} game={game} />
          })}
        </Box>
      ) : (
        /* Desktop Table View */
        <Paper sx={{ my: 2, overflowX: 'auto' }}>
          <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Time</TableCell>
              <TableCell align="center" sx={{ width: '50px' }}></TableCell>
              <TableCell>Home</TableCell>
              <TableCell align="center" sx={{ width: '50px' }}></TableCell>
              <TableCell>Away</TableCell>
              <TableCell>Venue</TableCell>
              <TableCell>Score</TableCell>
              <TableCell>Type</TableCell>
              {canShowActions && (
                <TableCell align="right" sx={{ width: '120px' }}>Actions</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {displayRows.map(game => {
              if (game._isSectionHeader) {
                return (
                  <TableRow key={game.id} data-testid="schedule-section-header">
                    <TableCell
                      component="th"
                      scope="colgroup"
                      colSpan={tableColumnCount}
                      sx={{
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        fontWeight: 700,
                        py: 1
                      }}
                    >
                      {game.section.label}
                    </TableCell>
                  </TableRow>
                )
              }
              const gameKey = getScheduleRowKey(game)
              if (game._isSocialEvent) {
                const scopeLabel = game.teamId
                  ? `Team: ${game.teamName || ''}`
                  : game.leagueId
                    ? `League: ${game.leagueName || ''}`
                    : ''
                const rsvp = socialRsvpState[game.socialEventId] ?? { attending: game.currentUserAttending, submitting: false, error: null }
                return (
                  <TableRow key={gameKey} sx={{ bgcolor: 'action.hover' }}>
                    <TableCell>{formatGameDate(game.start)}</TableCell>
                    <TableCell>{formatGameTime(game.start)}</TableCell>
                    <TableCell align="center" sx={{ width: '50px' }}>
                      <SocialEventIcon fontSize="small" color="secondary" />
                    </TableCell>
                    <TableCell colSpan={3}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {game.title}
                      </Typography>
                      {scopeLabel && (
                        <Typography variant="caption" color="text.secondary">
                          {scopeLabel}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{game.location || '–'}</TableCell>
                    <TableCell>
                      <Chip label="Social" size="small" color="secondary" variant="outlined" clickable={false} onClick={() => {}} />
                    </TableCell>
                    <TableCell>
                      {isAuthenticated && (
                        <Stack spacing={0.5}>
                          {rsvp.error && <Alert severity="error" sx={{ py: 0, fontSize: '0.75rem' }}>{rsvp.error}</Alert>}
                          {rsvp.attending === null ? (
                            <Stack direction="row" spacing={0.5}>
                              <Button variant="contained" color="success" size="small" disabled={rsvp.submitting}
                                onClick={() => handleSocialRsvp(game.socialEventId, true)}>
                                Going
                              </Button>
                              <Button variant="outlined" color="error" size="small" disabled={rsvp.submitting}
                                onClick={() => handleSocialRsvp(game.socialEventId, false)}>
                                Can&apos;t go
                              </Button>
                            </Stack>
                          ) : (
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <Chip label={rsvp.attending ? 'Going' : 'Not going'}
                                color={rsvp.attending ? 'success' : 'error'} size="small" clickable={false} onClick={() => {}} />
                              <Button variant="text" size="small" disabled={rsvp.submitting}
                                onClick={() => handleSocialRsvp(game.socialEventId, !rsvp.attending)}
                                sx={{ minWidth: 0 }}>
                                Change
                              </Button>
                            </Stack>
                          )}
                        </Stack>
                      )}
                    </TableCell>
                    {canShowActions && (
                      <TableCell align="right" sx={{ width: '120px' }} />
                    )}
                  </TableRow>
                )
              }
              if (game._isPlaceholder) {
                return (
                  <TableRow key={gameKey} sx={{ bgcolor: 'action.hover' }}>
                    <TableCell>{formatGameDate(game.start)}</TableCell>
                    <TableCell>{formatGameTime(game.start)}</TableCell>
                    <TableCell align="center" sx={{ width: '50px' }} />
                    <TableCell>
                      <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                        {game.homeTeamName}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ width: '50px' }} />
                    <TableCell>
                      <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                        {game.awayTeamName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {game.fieldName && game.venueName
                        ? `${game.fieldName} - ${game.venueName}`
                        : game.venueName || game.fieldName || '–'}
                    </TableCell>
                    <TableCell>
                      <Chip label="TBD" size="small" color="warning" variant="outlined" clickable={false} onClick={() => {}} />
                    </TableCell>
                    <TableCell><GameTypeBadge gameType={game.gameType ?? 0} /></TableCell>
                    {canShowActions && (
                      <TableCell align="right" sx={{ width: '120px' }}>
                        {canManageThisLeague && (
                          <>
                            <Tooltip title="Convert to game">
                              <IconButton size="small" onClick={() => handleConvertPlaceholder(game)} sx={{ padding: '4px' }}>
                                <ConvertIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit placeholder">
                              <IconButton size="small" onClick={() => handleEditPlaceholder(game)} sx={{ padding: '4px' }}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete placeholder">
                              <IconButton size="small" onClick={() => handleDeletePlaceholder(game.placeholderId)} sx={{ padding: '4px' }}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                )
              }
              const finished = isGameFinished(game)
              const byeGame = isByeGame(game)
              return (
                <TableRow key={gameKey}>
                  <TableCell>{formatGameDate(game.start)}</TableCell>
                  <TableCell>{formatGameTime(game.start)}</TableCell>
                  {/* Home Team Actions Column */}
                  <TableCell align="center" sx={{ width: '50px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                      {/* Show download game sheet for future games */}
                      {!byeGame && !finished && (
                        <Tooltip title={`Download ${game.homeTeamName} Game Sheet`}>
                          <IconButton
                            size="small"
                            onClick={() => downloadGameSheet(game.id || game.gameId, game.homeTeamId)}
                            sx={{ 
                              padding: '2px',
                              minWidth: '24px'
                            }}
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {/* Show match report button for finished games */}
                      {!byeGame && finished && (
                        hasMatchReport(game) ? (
                          <Tooltip title="View Match Report">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenReport(game.id || game.gameId, game.homeTeamId, game.homeTeamName)}
                              sx={{ 
                                padding: '2px',
                                minWidth: '24px'
                              }}
                            >
                              <ReportIcon fontSize="small" color="primary" />
                            </IconButton>
                          </Tooltip>
                        ) : canManageTeam(game.homeTeamId) && (
                          <Tooltip title={`Submit Match Report for ${game.homeTeamName}`}>
                            <IconButton
                              size="small"
                              onClick={() => {
                                navigate(
                                  `/games/${game.id || game.gameId}/teams/${game.homeTeamId}/report`
                                );
                              }}
                              sx={{ 
                                padding: '2px',
                                minWidth: '24px'
                              }}
                            >
                              <ReportIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )
                      )}
                    </Box>
                  </TableCell>
                  {/* Home Team Name Column */}
                  <TableCell>
                    <Link 
                      to={getTeamUrl(game.homeTeamId, game.homeTeamSlug)}
                      style={{
                        textDecoration: 'none',
                        color: 'inherit'
                      }}
                      onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                      onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <TeamJerseyColor jerseyColorHex={game.homeTeamJerseyColorHex} />
                        <Typography variant="body2">
                          {game.homeTeamName}
                        </Typography>
                      </Box>
                    </Link>
                  </TableCell>
                  {/* Away Team Actions Column */}
                  <TableCell align="center" sx={{ width: '50px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                      {/* Show download game sheet for future games */}
                      {!byeGame && !finished && (
                        <Tooltip title={`Download ${game.awayTeamName} Game Sheet`}>
                          <IconButton
                            size="small"
                            onClick={() => downloadGameSheet(game.id || game.gameId, game.awayTeamId)}
                            sx={{ 
                              padding: '2px',
                              minWidth: '24px'
                            }}
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {/* Show match report button for finished games */}
                      {!byeGame && finished && (
                        hasMatchReport(game) ? (
                          <Tooltip title="View Match Report">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenReport(game.id || game.gameId, game.awayTeamId, game.awayTeamName)}
                              sx={{ 
                                padding: '2px',
                                minWidth: '24px'
                              }}
                            >
                              <ReportIcon fontSize="small" color="primary" />
                            </IconButton>
                          </Tooltip>
                        ) : canManageTeam(game.awayTeamId) && (
                          <Tooltip title={`Submit Match Report for ${game.awayTeamName}`}>
                            <IconButton
                              size="small"
                              onClick={() => {
                                navigate(
                                  `/games/${game.id || game.gameId}/teams/${game.awayTeamId}/report`
                                );
                              }}
                              sx={{ 
                                padding: '2px',
                                minWidth: '24px'
                              }}
                            >
                              <ReportIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )
                      )}
                    </Box>
                  </TableCell>
                  {/* Away Team Name Column */}
                  <TableCell>
                    {byeGame ? (
                      <Typography variant="body2" color="text.secondary">
                        BYE
                      </Typography>
                    ) : (
                      <Link 
                        to={getTeamUrl(game.awayTeamId, game.awayTeamSlug)}
                        style={{
                          textDecoration: 'none',
                          color: 'inherit'
                        }}
                        onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                        onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <TeamJerseyColor jerseyColorHex={game.awayTeamJerseyColorHex} />
                          <Typography variant="body2">
                            {game.awayTeamName}
                          </Typography>
                        </Box>
                      </Link>
                    )}
                  </TableCell>
                  <TableCell>
                    {game.fieldName && game.venueName 
                      ? `${game.fieldName} - ${game.venueName}`
                      : game.venueName || game.fieldName || '–'
                    }
                  </TableCell>
                  <TableCell>
                    {!byeGame && game.homeScore != null && game.awayScore != null
                      ? `${game.homeScore} – ${game.awayScore}`
                      : '–'}
                  </TableCell>
                  <TableCell>
                    <GameTypeBadge gameType={game.gameType ?? 0} />
                  </TableCell>
                  {canShowActions && (
                    <TableCell align="right" sx={{ width: '120px' }}>
                      {/* Edit Game - Only for league managers or higher */}
                      {canManageLeague(leagueId || game.leagueId) && (
                        <Tooltip title="Edit Game">
                          <IconButton
                            onClick={() => {
                              navigate(`/games/${game.id || game.gameId}/edit`)
                            }}
                            size="small"
                            sx={{ 
                              padding: '4px',
                              minWidth: '28px'
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {/* Delete Game - Only for league managers or higher */}
                      {canManageLeague(leagueId || game.leagueId) && (
                        <Tooltip title="Delete Game">
                          <IconButton 
                            onClick={() => openDeleteDialog(game)}
                            size="small"
                            sx={{ 
                              padding: '4px',
                              minWidth: '28px'
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </Paper>
      )}

      {/* Pagination */}
      {shouldPaginate && totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 2 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            showFirstButton={!isMobile}
            showLastButton={!isMobile}
            siblingCount={isMobile ? 0 : 1}
            boundaryCount={1}
            size={isMobile ? "small" : "medium"}
          />
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        onConfirm={handleConfirmDelete}
        title="Delete Game?"
        itemName={
          gameToDelete
            ? getGameDeleteLabel(gameToDelete)
            : ''
        }
      />

      {/* Match Report View Dialog */}
      <MatchReportViewDialog
        open={reportDialogOpen}
        onClose={handleCloseReport}
        gameId={selectedReport.gameId}
        teamId={selectedReport.teamId}
        teamName={selectedReport.teamName}
        leagueId={selectedReport.leagueId}
      />

      {/* Schedule placeholder modals (league managers only) */}
      {canManageThisLeague && (
        <>
          <SchedulePlaceholderForm
            open={placeholderFormOpen}
            onClose={() => setPlaceholderFormOpen(false)}
            onSaved={handlePlaceholderSaved}
            leagueId={leagueId}
            placeholder={editingPlaceholder}
            fields={leagueVenuesAndFields.fields}
            venues={leagueVenuesAndFields.venues}
          />
          <ConvertPlaceholderDialog
            open={convertDialogOpen}
            onClose={() => setConvertDialogOpen(false)}
            onConverted={handlePlaceholderConverted}
            placeholder={convertingPlaceholder}
            teams={leagueTeams}
          />
        </>
      )}
    </>
  )
}

export default Schedule
